/**
 * BubbleFactory.js
 * Creates bubbles
 */

import { GameScene } from "pixi_core";

import { Common } from "./Common";
import { Bubble } from "./Bubble";
import { Grid } from "./Grid";
import { BubbleManager } from "./BubbleManager";

import { shuffle, BubbleColor, BubblePool, NB_MAX_COLUMN_INITIAL_SETUP, NB_MAX_COLUMN } from "../Constants";

export class BubbleFactory {
    /**
     * @public
     * @param {Grid} grid
     * @param {GameScene} scene
     * @param {BubbleManager} bubbleManager
     */
    constructor(grid, scene, bubbleManager) {
        // BubbleFactory is instantiated once and never destroyed
        Common.bubbleFactory = this;

        /** @private @type {import("../Constants").GenerationConstraints} */
        this._generationConstraints = null;

        /** @private @type {Grid} */
        this._grid = grid;

        /** @private @type {GameScene} */
        this._scene = scene;

        /** @private @type {BubbleManager} */
        this._bubbleManager = bubbleManager;

        /** @private @type {BubbleColor[]} */
        this._availableColor = [];

        /** @private @type {Number} */
        this._clusterOffset = 0;
    }

    /**
     * @public
     * @param {import("../Constants").GenerationConstraints} constraints
     */
    setConstraints(constraints) {
        this._generationConstraints = JSON.parse(JSON.stringify(constraints));

        // Special bubble odds
        let currentPercent = 0;
        let nbrRemaining = 0;
        this._generationConstraints.ColorlessPercent !== -1
            ? (currentPercent += this._generationConstraints.ColorlessPercent)
            : ++nbrRemaining;
        this._generationConstraints.StonePercent !== -1 ? (currentPercent += this._generationConstraints.StonePercent) : ++nbrRemaining;
        this._generationConstraints.HealPercent !== -1 ? (currentPercent += this._generationConstraints.HealPercent) : ++nbrRemaining;
        this._generationConstraints.ShieldedPercent !== -1
            ? (currentPercent += this._generationConstraints.ShieldedPercent)
            : ++nbrRemaining;
        this._generationConstraints.ChameleonPercent !== -1
            ? (currentPercent += this._generationConstraints.ChameleonPercent)
            : ++nbrRemaining;

        if (nbrRemaining !== 0) {
            const equalPercent = (100 - currentPercent) / nbrRemaining;
            if (this._generationConstraints.ColorlessPercent === -1) this._generationConstraints.ColorlessPercent = equalPercent;
            if (this._generationConstraints.StonePercent === -1) this._generationConstraints.StonePercent = equalPercent;
            if (this._generationConstraints.HealPercent === -1) this._generationConstraints.HealPercent = equalPercent;
            if (this._generationConstraints.ShieldedPercent === -1) this._generationConstraints.ShieldedPercent = equalPercent;
            if (this._generationConstraints.ChameleonPercent === -1) this._generationConstraints.ChameleonPercent = equalPercent;
        }

        // Set special to 0 if all special bubbles are set to 0
        if (currentPercent === 0 && nbrRemaining === 0) {
            this._generationConstraints.SpecialPercent = 0;
        }

        // Normal bubble odds
        currentPercent = 0;
        nbrRemaining = 0;
        this._generationConstraints.RedPercent !== -1 ? (currentPercent += this._generationConstraints.RedPercent) : ++nbrRemaining;
        this._generationConstraints.OrangePercent !== -1 ? (currentPercent += this._generationConstraints.OrangePercent) : ++nbrRemaining;
        this._generationConstraints.YellowPercent !== -1 ? (currentPercent += this._generationConstraints.YellowPercent) : ++nbrRemaining;
        this._generationConstraints.BluePercent !== -1 ? (currentPercent += this._generationConstraints.BluePercent) : ++nbrRemaining;
        this._generationConstraints.GreenPercent !== -1 ? (currentPercent += this._generationConstraints.GreenPercent) : ++nbrRemaining;
        this._generationConstraints.PurplePercent !== -1 ? (currentPercent += this._generationConstraints.PurplePercent) : ++nbrRemaining;
        this._generationConstraints.SpecialPercent !== -1 ? (currentPercent += this._generationConstraints.SpecialPercent) : ++nbrRemaining;

        if (nbrRemaining !== 0) {
            const equalPercent = (100 - currentPercent) / nbrRemaining;
            if (this._generationConstraints.RedPercent === -1) this._generationConstraints.RedPercent = equalPercent;
            if (this._generationConstraints.OrangePercent === -1) this._generationConstraints.OrangePercent = equalPercent;
            if (this._generationConstraints.YellowPercent === -1) this._generationConstraints.YellowPercent = equalPercent;
            if (this._generationConstraints.BluePercent === -1) this._generationConstraints.BluePercent = equalPercent;
            if (this._generationConstraints.GreenPercent === -1) this._generationConstraints.GreenPercent = equalPercent;
            if (this._generationConstraints.PurplePercent === -1) this._generationConstraints.PurplePercent = equalPercent;
            if (this._generationConstraints.SpecialPercent === -1) this._generationConstraints.SpecialPercent = equalPercent;
        }

        // Set availableColor
        this._availableColor = [];
        if (this._generationConstraints.RedPercent > 0) this._availableColor.push(BubbleColor.eRED);
        if (this._generationConstraints.OrangePercent > 0) this._availableColor.push(BubbleColor.eORANGE);
        if (this._generationConstraints.YellowPercent > 0) this._availableColor.push(BubbleColor.eYELLOW);
        if (this._generationConstraints.GreenPercent > 0) this._availableColor.push(BubbleColor.eGREEN);
        if (this._generationConstraints.BluePercent > 0) this._availableColor.push(BubbleColor.eBLUE);
        if (this._generationConstraints.PurplePercent > 0) this._availableColor.push(BubbleColor.ePURPLE);
        this._availableColor = shuffle(this._availableColor);
    }

    /**
     * @public
     * @param {Object} position
     * @param {{color: BubbleColor, special: BubbleColor}} color
     * @param {Object} gridPos
     * @param {BubblePool} pool
     * @param {Boolean} countdownStarted
     *
     * @returns {Bubble}
     */
    createBubble(position, color, gridPos, pool, countdownStarted = false) {
        /** @type {BubblePool} */
        let bubblePool = BubblePool.eGRID;
        /** @type {BubbleColor} */
        let bubbleType = BubbleColor.eUNDEFINED;
        /** @type {BubbleColor} */
        let specialType = BubbleColor.eUNDEFINED;

        if (color.color !== BubbleColor.eUNDEFINED) {
            if (pool === BubblePool.eCANNON) {
                bubblePool = BubblePool.eCANNON;
                bubbleType = this._randomizeShooterBubbleColour(color.color);

                if (color.special === BubbleColor.eCOLORLESS) {
                    specialType = color.special;
                }
            }

            // Determine bubble type and special type
            if (pool === BubblePool.eGRID) {
                if (color.color === BubbleColor.eRANDOM) {
                    bubbleType = this._randomizeBubbleColour(true);

                    if (bubbleType === BubbleColor.eCHAMELEON) {
                        bubbleType = this._randomizeBubbleColour(false);
                        specialType = BubbleColor.eCHAMELEON;
                    } else if (bubbleType === BubbleColor.eHEAL) {
                        bubbleType = this._randomizeBubbleColour(false);
                        specialType = BubbleColor.eHEAL;
                    } else if (bubbleType === BubbleColor.eSHIELDED) {
                        bubbleType = this._randomizeBubbleColour(false);
                        specialType = BubbleColor.eSHIELDED;
                    } else {
                        specialType = color.special;
                    }
                } else if (color.color === BubbleColor.eRANDOM_EDITOR) {
                    if (
                        color.special === BubbleColor.eUNDEFINED ||
                        color.special === BubbleColor.eRANDOM ||
                        color.special === BubbleColor.eHEAL ||
                        color.special === BubbleColor.eSHIELDED ||
                        color.special === BubbleColor.eCHAMELEON
                    ) {
                        bubbleType = this._randomizeBubbleColour(false);

                        // Change the random bubble color if it's about to make a cluster of over MaxConnected
                        if (gridPos.column > 0 || gridPos.row > 0) {
                            const cluster = this._bubbleManager.findCluster(
                                { color: bubbleType, column: gridPos.column, row: gridPos.row },
                                bubbleType
                            );
                            if (cluster.length > this._generationConstraints.MaxConnected) {
                                bubbleType = this.maxConnectedColorChange(bubbleType, gridPos.column, gridPos.row);
                            }
                        }
                    } else {
                        // Else random bubble is part of a cluster
                        bubbleType = this._randomizeRandomClusterBubbleColour(color.special);
                    }
                    specialType =
                        color.special === BubbleColor.eRANDOM || color.special === BubbleColor.eRANDOM_EDITOR
                            ? this._randomizeBubbleColour(false)
                            : color.special;
                } else {
                    bubbleType = color.color;

                    if (bubbleType === BubbleColor.eCHAMELEON && color.special === BubbleColor.eUNDEFINED) {
                        bubbleType = this._randomizeBubbleColour(false);
                        specialType = BubbleColor.eCHAMELEON;
                    } else if (bubbleType === BubbleColor.eHEAL && color.special === BubbleColor.eUNDEFINED) {
                        bubbleType = this._randomizeBubbleColour(false);
                        specialType = BubbleColor.eHEAL;
                    } else if (bubbleType === BubbleColor.eSHIELDED && color.special === BubbleColor.eUNDEFINED) {
                        specialType = BubbleColor.eSHIELDED;
                    } else if (bubbleType === BubbleColor.eBLOOMING && color.special === BubbleColor.eUNDEFINED) {
                        specialType = BubbleColor.eBLOOMING;
                    } else if (bubbleType === BubbleColor.eSTONE_BLOOMING && color.special === BubbleColor.eUNDEFINED) {
                        specialType = BubbleColor.eSTONE_BLOOMING;
                    } else {
                        specialType =
                            color.special === BubbleColor.eRANDOM || color.special === BubbleColor.eRANDOM_EDITOR
                                ? this._randomizeBubbleColour(false)
                                : color.special;
                    }
                }
            }

            // Already a bubble at the specified grid position?
            if (pool === BubblePool.eGRID && this._grid.getBubble(gridPos.column, gridPos.row) !== null) return null;

            const bubble = new Bubble(position, bubbleType, specialType, bubblePool, this._scene, gridPos, countdownStarted);

            if (pool === BubblePool.eGRID) {
                this._grid.clipBubblePositionToGrid(bubble);
                Common.collisions.addBubble(bubble);
            }

            return bubble;
        } else {
            // Undefined color
            return null;
        }
    }

    /**
     * Change the initial color to another one available in the level
     * @param {BubbleColor} initialColor
     * @returns {BubbleColor}
     */
    maxConnectedColorChange(initialColor, column, row) {
        const availableColor = JSON.parse(JSON.stringify(this._availableColor));

        let colorToRemove = initialColor;
        let clusterSize = 0;
        let random = 0;
        do {
            availableColor.splice(availableColor.indexOf(colorToRemove), 1);
            random = Math.floor(Math.random() * availableColor.length);

            colorToRemove = availableColor[random];
            if (availableColor.length === 1) break;

            clusterSize = this._bubbleManager.findCluster(
                { color: availableColor[random], column: column, row: row },
                availableColor[random]
            ).length;
        } while (clusterSize > this._generationConstraints.MaxConnected);

        return availableColor[random];
    }

    /**
     * @private
     * @param {Boolean} containSpecials Can this random bubble be a specials bubble?
     * @returns {BubbleColor}
     */
    _randomizeBubbleColour(containSpecials) {
        // Odds for basic colors
        let maxPercent = 100;
        if (!containSpecials) maxPercent -= this._generationConstraints.SpecialPercent;

        const randomNbr = Math.random() * maxPercent;
        let currentOdds = 0;

        currentOdds += this._generationConstraints.RedPercent;
        if (randomNbr <= currentOdds) return BubbleColor.eRED;
        currentOdds += this._generationConstraints.OrangePercent;
        if (randomNbr <= currentOdds) return BubbleColor.eORANGE;
        currentOdds += this._generationConstraints.YellowPercent;
        if (randomNbr <= currentOdds) return BubbleColor.eYELLOW;
        currentOdds += this._generationConstraints.BluePercent;
        if (randomNbr <= currentOdds) return BubbleColor.eBLUE;
        currentOdds += this._generationConstraints.GreenPercent;
        if (randomNbr <= currentOdds) return BubbleColor.eGREEN;
        currentOdds += this._generationConstraints.PurplePercent;
        if (randomNbr <= currentOdds) return BubbleColor.ePURPLE;

        // Odds for special types
        const randomNbr2 = Math.random() * 100;
        currentOdds = 0;

        currentOdds += this._generationConstraints.ColorlessPercent;
        if (randomNbr2 <= currentOdds) return BubbleColor.eCOLORLESS;
        currentOdds += this._generationConstraints.StonePercent;
        if (randomNbr2 <= currentOdds) return BubbleColor.eSTONE;
        currentOdds += this._generationConstraints.HealPercent;
        if (randomNbr2 <= currentOdds) return BubbleColor.eHEAL;
        currentOdds += this._generationConstraints.ShieldedPercent;
        if (randomNbr2 <= currentOdds) return BubbleColor.eSHIELDED;
        currentOdds += this._generationConstraints.ChameleonPercent;
        if (randomNbr2 <= currentOdds) return BubbleColor.eCHAMELEON;
    }

    /**
     * @private
     * @param {BubbleColor} color
     */
    _randomizeShooterBubbleColour(color) {
        /** @type {BubbleColor} */
        let finalColour = BubbleColor.eUNDEFINED;

        if (color === BubbleColor.eRANDOM) {
            //Random Bubble color taking available color into account
            const random = Math.floor(Math.random() * this._availableColor.length);
            finalColour = this._availableColor[random];
        } //Colored bubble with the color offset
        else finalColour = color;

        return finalColour;
    }

    /**
     * Return a color different from the provided color considering the level available colors
     * @public
     * @param {BubbleColor} currentColor
     * @returns {BubbleColor}
     */
    randomizeChameleonColor(currentColor) {
        /** @type {Number[]} */
        const availableColor = JSON.parse(JSON.stringify(this._availableColor));
        const index = availableColor.indexOf(currentColor);
        if (index !== -1) availableColor.splice(index, 1);

        const random = Math.floor(Math.random() * availableColor.length);

        return this._availableColor.length > 0 ? availableColor[random] : currentColor;
    }

    /**
     * Set random bubble color based on their matching cluster
     * @private
     * @param {BubbleColor} color
     */
    _randomizeRandomClusterBubbleColour(color) {
        const correspondingColor = (color + this._clusterOffset) % this._availableColor.length;
        return this._availableColor[correspondingColor];
    }

    /**
     * @public
     * @param {Object} bubbleLayout
     */
    generateInitialLayout(bubbleLayout) {
        this._grid.resetGridOffset();

        // Change the color for matching random clusters
        this._clusterOffset = Math.floor(Math.random() * this._availableColor.length);

        for (let column = 0; column < NB_MAX_COLUMN_INITIAL_SETUP; column++) {
            const maxRow = this._grid.getNbRows(column);
            for (let row = 0; row < maxRow; row++) {
                const color = bubbleLayout[column][row];
                if (color !== BubbleColor.eEMPTY) {
                    this.createBubble(
                        this._grid.convertGridPosToScreenPos(column, row),
                        color,
                        { column: column, row: row },
                        BubblePool.eGRID,
                        true
                    );
                }
            }
        }

        this._validateMinimumMatch(bubbleLayout);
    }

    /**
     * @public
     * @param {Object} bubbleLayout
     * @param {Function} callback
     */
    generateRespawnLayout(bubbleLayout, callback) {
        // Change the color for matching random clusters
        this._clusterOffset = Math.floor(Math.random() * this._availableColor.length);

        this._grid.resetGridOffset();

        // Determine the last column with bubbles to assign the completion callback
        let lastColumnWithBubbles = 0;
        for (let column = 0; column < bubbleLayout.length; ++column) {
            const nbBubbles = this._nbBubbleInRespawnColumn(bubbleLayout, column);
            if (nbBubbles > 0) {
                lastColumnWithBubbles = column;
            }
        }

        for (let column = 0; column < bubbleLayout.length; ++column) {
            if (column === lastColumnWithBubbles) {
                // Monitor completion on last column with bubbles
                this._createRespawnColumn(bubbleLayout, column, callback);
            } else {
                this._createRespawnColumn(bubbleLayout, column);
            }
        }
    }

    /**
     * Returns the number of bubbles in a respawn layout column
     * @private
     * @param {Object} bubbleLayout
     * @param {Number} column - Column index
     * @returns {Number}
     */
    _nbBubbleInRespawnColumn(bubbleLayout, column) {
        let nbBubbles = 0;

        const columnSize = bubbleLayout[column].length;
        for (let row = 0; row < columnSize; ++row) {
            const color = bubbleLayout[column][row];
            if (color !== BubbleColor.eEMPTY) {
                ++nbBubbles;
            }
        }

        return nbBubbles;
    }

    /**
     * Creates a respawn layout column
     * @private
     * @param {Object} bubbleLayout
     * @param {Number} column - Column index
     * @param {Function} [callback] - Completion callback
     */
    _createRespawnColumn(bubbleLayout, column, callback) {
        const columnSize = bubbleLayout[column].length;

        const bubbleToSpawn = [];

        for (let row = 0; row < columnSize; ++row) {
            const color = bubbleLayout[column][row];
            if (color !== BubbleColor.eEMPTY) {
                const bubble = this.createBubble(
                    this._grid.convertGridPosToScreenPos(column, row),
                    color,
                    { column: column, row: row },
                    BubblePool.eGRID
                );
                if (bubble) {
                    bubbleToSpawn.push(bubble);
                }
            }
        }

        // Monitor spawn completion on last bubble
        if (bubbleToSpawn.length > 0) {
            for (let i = 0; i < bubbleToSpawn.length; ++i) {
                if (i === bubbleToSpawn.length - 1) {
                    bubbleToSpawn[i].spawn(callback);
                } else {
                    bubbleToSpawn[i].spawn();
                }
            }
        } else {
            if (callback) callback();
        }
    }

    /**
     * Validate that the inital minimal number of match is respected
     * @param {Object} bubbleLayout
     */
    _validateMinimumMatch(bubbleLayout) {
        // Init possible match array
        const minMatch = this._generationConstraints.MinMatch;
        const minMatchOf = this._generationConstraints.MinMatchOf;
        /** @type {Array[]} */
        const possibleMatch = [];
        for (let i = 0; i <= minMatchOf; ++i) {
            possibleMatch.push([]);
        }

        // Fill possible match array with all possible matches by number of bubbles in the match
        for (let column = 0; column < NB_MAX_COLUMN_INITIAL_SETUP; ++column) {
            const maxRow = this._grid.getNbRows(column);
            for (let row = 0; row < maxRow; row++) {
                // Only look at starting point which were random in the editor
                if (
                    bubbleLayout[column][row].color === BubbleColor.eRANDOM_EDITOR &&
                    bubbleLayout[column][row].special === BubbleColor.eRANDOM
                ) {
                    const bubble = this._grid.getBubble(column, row);
                    const match = this._bubbleManager.findInitialMatch(bubble, bubble.color);
                    if (match) {
                        const matchSize = match.length >= minMatchOf ? minMatchOf : match.length;
                        possibleMatch[matchSize].push(match);
                    }
                }
            }
        }

        // Check if enough matches are already present
        const correctMatchNbr = possibleMatch[minMatchOf].length;
        if (correctMatchNbr >= minMatch) return;

        // Number of match to force
        let forcedMatchRemaining = minMatch - correctMatchNbr;
        // Start looking for starting points with the most bubbles already
        let currentSize = minMatchOf - 1;

        // Force matches until minMatch
        while (forcedMatchRemaining > 0) {
            // Init for revert on bad match
            const bubbleChanged = [];
            possibleMatch[currentSize] = possibleMatch[currentSize].filter((element) => element.length !== 0);
            if (possibleMatch[currentSize].length === 0) {
                --currentSize;
                if (currentSize === 0) break;
                continue;
            }

            // Choose a random point of start
            const randomIndex = Math.floor(Math.random() * possibleMatch[currentSize].length);
            const cluster = possibleMatch[currentSize][randomIndex];
            const clusterColor = cluster[0].color;

            // Init checks for bubbles already processed
            const testedNeighborCheck = [];
            for (let column = 0; column < NB_MAX_COLUMN; ++column) {
                testedNeighborCheck.push([]);
                const maxRow = this._grid.getNbRows(column);
                for (let row = 0; row < maxRow; ++row) {
                    testedNeighborCheck[column].push(false);
                }
            }
            const bubblesToChangeCheck = [];
            for (let column = 0; column < NB_MAX_COLUMN; ++column) {
                bubblesToChangeCheck.push([]);
                const maxRow = this._grid.getNbRows(column);
                for (let row = 0; row < maxRow; ++row) {
                    bubblesToChangeCheck[column].push(false);
                }
            }

            // Number of bubbles to add
            let remainingBubbleToAdd = minMatchOf - currentSize;
            let keepGoing = true;
            // Add bubbles to selected match until the match meet minMatchOf
            while (remainingBubbleToAdd > 0 && keepGoing) {
                keepGoing = false;

                // Init list of all starting points
                const allNeighbors = [];
                for (let i = 0; i < minMatchOf; ++i) {
                    allNeighbors.push([]);
                }
                const allNeighborsCheck = [];
                for (let column = 0; column < NB_MAX_COLUMN; ++column) {
                    allNeighborsCheck.push([]);
                    const maxRow = this._grid.getNbRows(column);
                    for (let row = 0; row < maxRow; ++row) {
                        allNeighborsCheck[column].push(false);
                    }
                }

                // Find the possible starting points
                for (const bubble of cluster) {
                    const neighbor = this._grid.getNeighborBubbles(bubble.column, bubble.row);
                    const filteredNeighbor = neighbor.filter((element) => element.color !== clusterColor);
                    for (const neighborBubble of filteredNeighbor) {
                        const initialClusterSize = neighborBubble.initialClusterSize;
                        if (
                            initialClusterSize < minMatchOf &&
                            !allNeighborsCheck[neighborBubble.column][neighborBubble.row] &&
                            !testedNeighborCheck[neighborBubble.column][neighborBubble.row] &&
                            bubbleLayout[neighborBubble.column][neighborBubble.row].color === BubbleColor.eRANDOM_EDITOR &&
                            bubbleLayout[neighborBubble.column][neighborBubble.row].special === BubbleColor.eRANDOM
                        ) {
                            allNeighbors[initialClusterSize].push(neighborBubble);
                            allNeighborsCheck[neighborBubble.column][neighborBubble.row] = true;
                        }
                        testedNeighborCheck[neighborBubble.column][neighborBubble.row] = true;
                    }
                }

                // Prevent infinite loop when the specified match requirement is not possible
                if (allNeighbors.filter((element) => element.length !== 0).length === 0) {
                    keepGoing = false;
                    continue;
                }

                // Go through neighbor clusters, in order of initialClusterSize
                for (const neighborCluster of allNeighbors) {
                    if (neighborCluster.length === 0) continue;

                    // Try to add bubble at neighbor, in no particular order
                    for (const neighborBubble of neighborCluster) {
                        // Skip duplicate bubble (duplicates are most probably already filtered by this point though)
                        if (!bubblesToChangeCheck[neighborBubble.column][neighborBubble.row]) {
                            // Test to make sure the added bubble doesn't link to too many bubble of the same color
                            const tmpCluster = this._bubbleManager.findCluster(
                                { color: clusterColor, column: neighborBubble.column, row: neighborBubble.row },
                                clusterColor
                            );
                            const expected = minMatchOf - remainingBubbleToAdd + 1;
                            const tmpSize = tmpCluster.length;

                            // Cancel when too big
                            if (tmpSize >= this._generationConstraints.MaxConnected) continue;
                            // Cancel when it's about to use an already good match cluster
                            if (tmpSize - expected >= minMatchOf) continue;

                            // Adjust remaining when linked to a new cluster
                            if (tmpSize > expected) remainingBubbleToAdd -= tmpSize - expected;

                            // Start bubble change
                            bubblesToChangeCheck[neighborBubble.column][neighborBubble.row] = true;
                            keepGoing = true;
                            --remainingBubbleToAdd;

                            // Apply the color change to grid
                            if (neighborBubble.color !== clusterColor) {
                                bubbleChanged.push({
                                    column: neighborBubble.getGridPos().column,
                                    row: neighborBubble.getGridPos().row,
                                    color: neighborBubble.color
                                });

                                const newBubble =
                                    neighborBubble.specialType === BubbleColor.eHEAL
                                        ? this.createNewBubbleWithColor(neighborBubble, { color: clusterColor, special: BubbleColor.eHEAL })
                                        : this.createNewBubbleWithColor(neighborBubble, { color: clusterColor, special: clusterColor });
                                newBubble.initialClusterSize = tmpSize;
                            }

                            // Update possibleMatch array to reflect changes to that bubble
                            for (let i = 0; i < possibleMatch[neighborBubble.initialClusterSize].length; ++i) {
                                if (possibleMatch[neighborBubble.initialClusterSize][i].includes(neighborBubble)) {
                                    const tmpMatch = possibleMatch[neighborBubble.initialClusterSize].splice(i, 1);
                                    tmpMatch.splice(tmpMatch.indexOf(neighborBubble), 1);
                                    tmpMatch.forEach((element) => --element.initialClusterSize);
                                    if (neighborBubble.initialClusterSize > 1) {
                                        possibleMatch[neighborBubble.initialClusterSize - 1].push(tmpMatch);
                                    }
                                    break;
                                }
                            }

                            // Match requirement reached
                            if (remainingBubbleToAdd < 1) {
                                // Remove this now completed match from possible match
                                possibleMatch[currentSize].splice(randomIndex, 1);

                                // Set initialClusterSize for bubbles in completed match
                                for (const bubble of tmpCluster) {
                                    bubble.initialClusterSize = tmpSize;
                                }

                                // Go to next iteration
                                --forcedMatchRemaining;
                                break;
                            }
                        }
                    }
                    // Go to next iteration
                    if (remainingBubbleToAdd < 1) break;
                }
            }

            // Bad starting point
            if (remainingBubbleToAdd > 0) {
                // Remove this failed match from possible match
                possibleMatch[currentSize].splice(randomIndex, 1);

                keepGoing = false;

                // Revert changes in case of failed attempt
                for (const bubble of bubbleChanged) {
                    const changedBubble = this._grid.getBubble(bubble.column, bubble.row);
                    changedBubble.specialType === BubbleColor.eHEAL
                        ? this.createNewBubbleWithColor(changedBubble, { color: bubble.color, special: BubbleColor.eHEAL })
                        : this.createNewBubbleWithColor(changedBubble, { color: bubble.color, special: bubble.color });
                }
            }
        }
    }

    /**
     * Change the color of a bubble in the grid
     * @param {Bubble} oldBubble
     * @param {{color: BubbleColor, special: BubbleColor}} newColor
     * @returns {Bubble}
     */
    createNewBubbleWithColor(oldBubble, newColor) {
        const position = JSON.parse(JSON.stringify(oldBubble.getPos()));
        const gridPos = JSON.parse(JSON.stringify(oldBubble.getGridPos()));
        Common.collisions.removeBubble(oldBubble);
        this._grid.removeBubble(oldBubble);
        oldBubble.destroy();

        return this.createBubble(position, newColor, gridPos, BubblePool.eGRID);
    }

    /**
     * @public
     */
    createLine() {
        this._grid.pushGridLeft();

        const maxRow = this._grid.getNbRows(0);
        for (let row = 0; row < maxRow; ++row) {
            const bubble = this.createBubble(
                this._grid.convertGridPosToScreenPos(0, row),
                { color: BubbleColor.eRANDOM, special: BubbleColor.eRANDOM },
                { column: 0, row: row },
                BubblePool.eGRID
            );
            if (bubble) bubble.spawn();
        }
    }

    /**
     * @public
     */
    destroy() {
        this._generationConstraints = null;
        this._grid = null;
        this._scene = null;
        this._bubbleManager = null;
    }
}
